/*
if type == FIXED -> target = number
if type == SENSOR -> target = {sensorId: string, infoName: string}
*/
import {arrayIsSet, objectIsSet} from "../providers/service/global-variables.service";

export enum PidConfigurationTargetTypeEnum {
    FIXED,
    SENSOR,
    SCENARIO
}

export enum PidConfigurationSeasonEnum {
    WINTER,
    SUMMER,
    ANY
}

export enum PidConfigurationTargetOpEnum {
    EQUAL,
    GREATER,
    LESS
}

export class PidInput {
    constructor(sensorId: string, infoName: string, type: PidConfigurationTargetTypeEnum | undefined, json: any | undefined = undefined) {
        this._sensorId = sensorId;
        this._infoName = infoName;
        if (type) {
            this.type = type
        } else {
            this.type = PidConfigurationTargetTypeEnum.FIXED
        }

        if (json != null) {
            Object.keys(json)
                .filter(key => {
                    const notKeys = ['sensorId', 'infoName', 'type']
                    return !notKeys.includes(key) && key in this
                }).forEach(key => {
                if (json[key] != null) {
                    (this as any)[key] = json[key]
                }
            })
        }
    }

    private _infoName: string;
    private _i_max?: number;
    private _k_p?: number;
    private _k_i?: number;
    private _k_d?: number
    private _sensorId: string;
    private _type: PidConfigurationTargetTypeEnum = PidConfigurationTargetTypeEnum.FIXED;
    private _targetTypeFixed?: number;
    private _targetTypeSensor?: { sensorId: string, infoName: string };
    private _targetScenario?: { sensorId: string, };
    private _target_op?: PidConfigurationTargetOpEnum;
    private _max_step_up?: number;
    private _max_step_down?: number;

    get infoName() {
        return this._infoName
    }

    set infoName(value) {
        this._infoName = value;
    }

    get i_max() {
        return this._i_max
    }

    set i_max(value) {
        this._i_max = value;
    }

    get k_p() {
        return this._k_p
    }

    set k_p(value) {
        this._k_p = value;
    }

    get k_i() {
        return this._k_i
    }

    set k_i(value) {
        this._k_i = value;
    }

    get k_d() {
        return this._k_d
    }


    set k_d(value) {
        this._k_d = value;
    }

    get max_step_down() {
        return this._max_step_down
    }

    set max_step_down(value) {
        this._max_step_down = value
    }

    get max_step_up() {
        return this._max_step_up
    }

    set max_step_up(value) {
        this._max_step_up = value
    }

    get target_op() {
        return this._target_op
    }

    set target_op(value) {
        this._target_op = value
    }

    get target() {
        if (this.type === PidConfigurationTargetTypeEnum.FIXED) {
            return this.targetFixed
        } else if (this.type === PidConfigurationTargetTypeEnum.SENSOR) {
            return this.targetSensor
        } else {
            return this.targetScenario
        }
    }

    get targetFixed() {
        return this._targetTypeFixed
    }

    get targetSensor() {
        return this._targetTypeSensor
    }

    get targetScenario() {
        return this._targetScenario
    }

    set target(value: any) {
        if (this.type === PidConfigurationTargetTypeEnum.FIXED) {
            this._targetTypeFixed = value;
        } else if (this.type === PidConfigurationTargetTypeEnum.SENSOR) {
            this._targetTypeSensor = {sensorId: value.sensorId, infoName: value.infoName}
        } else if (this.type === PidConfigurationTargetTypeEnum.SCENARIO) {
            this._targetScenario = {sensorId: value.sensorId}
        }
    }

    get sensorId() {
        return this._sensorId
    }

    set sensorId(value) {
        this._sensorId = value
    }

    get type() {
        return this._type
    }

    set type(value) {
        this._type = value
    }

    toJSON() {
        return ['max_step_down', 'max_step_up', 'target_op', 'target', 'type', 'k_d', 'k_i', 'k_p', 'i_max', 'infoName', 'sensorId'].reduce((prev, key) => {
            prev[key] = (this as any)[key];
            return prev
        }, {} as any)
    }
}


export class PidActuator {
    constructor(sensorId: string, command: string, infoName: string, maxValue: number | undefined = undefined, minValue: number | undefined = undefined) {
        this._sensorId = sensorId;
        this._command = command;
        this._infoName = infoName;
        this.minValue = minValue
        this.maxValue = maxValue


    }

    private _sensorId: string;
    private _command: string;
    private _infoName: string;
    private _maxValue?: number;
    private _minValue?: number

    get sensorId() {
        return this._sensorId
    }

    set sensorId(value) {
        this._sensorId = value
    }

    get command() {
        return this._command
    }

    set command(value) {
        this._command = value
    }

    get infoName() {
        return this._infoName
    }

    set infoName(value) {
        this._infoName = value
    }

    get minValue() {
        return this._minValue
    }

    set minValue(value) {
        this._minValue = value
    }

    get maxValue() {
        return this._maxValue
    }

    set maxValue(value) {
        this._maxValue = value
    }

    toJSON() {
        return ['maxValue', 'minValue', 'infoName', 'command', 'sensorId'].reduce((prev, key) => {
            prev[key] = (this as any)[key];
            return prev
        }, {} as any)
    }
}

export class PidConfiguration {
    private _inputs?: PidInput[];
    private _actuator?: PidActuator;
    private _projectId?: string;
    private _ms_between_executions?: number
    private _season: PidConfigurationSeasonEnum = PidConfigurationSeasonEnum.ANY

    constructor(json: any) {
        if (json != null) {
            Object.keys(json)
                .forEach(key => {
                    if (key == 'inputs') {
                        if (arrayIsSet(json.inputs)) {
                            const inputsJson: any = json.inputs
                            this.inputs = inputsJson.map((inputJson: any) => new PidInput(inputJson.sensorId, inputJson.infoName, inputJson.type, inputJson))
                        }
                    } else if (key == 'actuator') {
                        if (objectIsSet(json.actuator)) {
                            const actuator: any = json.actuator
                            this.actuator = new PidActuator(actuator.sensorId, actuator.command, actuator.infoName, actuator.maxValue, actuator.minValue)
                        }
                    } else if (key in this) {
                        (this as any) [key] = json[key];
                    }
                })
        }
    }

    public set actuator(value) {
        this._actuator = value;
    }

    public get actuator() {
        return this._actuator;
    }

    public set inputs(value) {
        this._inputs = value;
    }

    public get inputs() {
        return this._inputs;
    }

    get ms_between_executions() {
        return this._ms_between_executions
    }


    set ms_between_executions(value) {
        this._ms_between_executions = value;
    }

    public set projectId(value) {
        this._projectId = value;
    }

    public get projectId() {
        return this._projectId;
    }

    get season() {
        return this._season
    }

    set season(value) {
        this._season = value
    }

    toJSON() {
        return ['inputs', 'projectId', 'actuator', 'ms_between_executions', 'season'].reduce((prev, key) => {
            if (key == 'inputs') {
                prev[key] = this.inputs?.map(input => {
                    return input.toJSON()
                })
            } else if (key == 'actuator') {
                prev[key] = this.actuator?.toJSON()
            } else {
                prev[key] = (this as any)[key];
            }
            return prev
        }, {} as any)
    }

}

